/**
 * Residual TARA API
 *
 * Wraps `/api/v1/residual/{sessionId}/*` endpoints — initialize, progress
 * fetch, control implementation, risk decisions (bulk + per-risk),
 * requirement fulfillment, and the workflow `continue` action. The
 * shared axios client has baseURL `/api/v1`.
 */

import client from './client';

/**
 * Initialize residual TARA for a session.
 * Mirrors AthenaBackendService.initializeResidualTARA(sessionId).
 */
export async function initializeResidualTARA(sessionId) {
  return client.post(`/residual/${sessionId}/initialize`);
}

/**
 * Get the residual TARA progress document.
 * Mirrors AthenaBackendService.getResidualProgress(sessionId).
 *
 * Preserves the legacy contract: returns `null` on 404 (residual not yet
 * initialized) or on a non-JSON response. Other errors propagate.
 */
export async function getResidualProgress(sessionId) {
  try {
    const result = await client.get(`/residual/${sessionId}`);
    return result ?? null;
  } catch (error) {
    if (error?.status === 404) return null;
    throw error;
  }
}

/**
 * Update the implementation status of a single security control.
 * Mirrors AthenaBackendService.updateControlImplementation(sessionId, controlId, updateData).
 */
export async function updateControlImplementation(sessionId, controlId, updateData) {
  return client.post(`/residual/${sessionId}/controls/${controlId}`, updateData);
}

/**
 * Submit a (bulk) risk decision — accept / reject / etc.
 * Mirrors AthenaBackendService.submitRiskDecision(sessionId, decision).
 */
export async function submitRiskDecision(sessionId, decision) {
  return client.post(`/residual/${sessionId}/risk-decision`, decision);
}

/**
 * Continue the residual TARA workflow state machine.
 * Mirrors AthenaBackendService.continueResidualWorkflow(sessionId).
 */
export async function continueResidualWorkflow(sessionId) {
  return client.post(`/residual/${sessionId}/continue`);
}

/**
 * Update requirement fulfillment status for a single requirement.
 * Mirrors AthenaBackendService.updateRequirementFulfillment(sessionId, requirementId, updateData).
 */
export async function updateRequirementFulfillment(sessionId, requirementId, updateData) {
  return client.post(
    `/residual/${sessionId}/requirements/${requirementId}`,
    updateData
  );
}

/**
 * Submit an individual risk-treatment decision (Avoid / Share / Retain
 * / Reduce — the `decision` shape varies; passed through unchanged).
 * Mirrors AthenaBackendService.submitIndividualRiskDecision(sessionId, riskId, decision).
 */
export async function submitIndividualRiskDecision(sessionId, riskId, decision) {
  return client.post(
    `/residual/${sessionId}/risks/${riskId}/decision`,
    decision
  );
}
